import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { SEED_ARTICLES, SEED_PRODUCTS, SEED_VIDEOS } from '../data/seedData';
import { fbGet, fbAdd, fbUpdate, fbDelete, auth, fbUtils } from '../firebase/config';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [articles, setArticles] = useState(SEED_ARTICLES.filter(a => a.status === 'published'));
  const [allArticles, setAllArticles] = useState(SEED_ARTICLES);
  const [products, setProducts] = useState(SEED_PRODUCTS);
  const [videos, setVideos] = useState(SEED_VIDEOS);
  const [documents, setDocuments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [adminUser, setAdminUser] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, emoji = '✅', status = 'success') => {
    setToast({ message, emoji, status });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadData = useCallback(async () => {
    const [fbArticles, fbProducts, fbVideos, fbDocs, fbSubs, fbContacts] = await Promise.all([
      fbGet('articles'),
      fbGet('products'),
      fbGet('videos'),
      fbGet('documents'),
      fbGet('subscribers'),
      fbGet('contacts'),
    ]);
    if (fbArticles?.length) {
      setAllArticles(fbArticles);
      setArticles(fbArticles.filter(a => a.status === 'published'));
    }
    if (fbProducts?.length) setProducts(fbProducts);
    if (fbVideos?.length) setVideos(fbVideos);
    if (fbDocs) setDocuments(fbDocs);
    if (fbSubs) setSubscribers(fbSubs);
    if (fbContacts) setContacts(fbContacts);
  }, []);

  useEffect(() => {
    loadData();
    if (auth && fbUtils?.onAuthStateChanged) {
      try {
        const unsub = fbUtils.onAuthStateChanged(auth, user => setAdminUser(user));
        return () => unsub();
      } catch (e) {}
    }
  }, [loadData]);

  // Article operations
  const saveArticle = useCallback(async (data, editingId = null) => {
    if (editingId) {
      await fbUpdate('articles', editingId, data);
      setAllArticles(prev => prev.map(a => a.id === editingId ? { ...a, ...data } : a));
      setArticles(prev => prev.map(a => a.id === editingId ? { ...a, ...data } : a));
      showToast('Article updated!', '✅');
      return editingId;
    } else {
      const id = await fbAdd('articles', data) || `local_${Date.now()}`;
      const newArticle = { id, ...data };
      setAllArticles(prev => [newArticle, ...prev]);
      if (data.status === 'published') setArticles(prev => [newArticle, ...prev]);
      showToast('Article published!', '🚀');
      return id;
    }
  }, [showToast]);

  const deleteArticle = useCallback(async (id) => {
    await fbDelete('articles', id);
    setAllArticles(prev => prev.filter(a => a.id !== id));
    setArticles(prev => prev.filter(a => a.id !== id));
    showToast('Article deleted.', '🗑️');
  }, [showToast]);

  // Product operations
  const saveProduct = useCallback(async (data, editingId = null) => {
    if (editingId) {
      await fbUpdate('products', editingId, data);
      setProducts(prev => prev.map(p => p.id === editingId ? { ...p, ...data } : p));
      showToast('Product updated!', '✅');
    } else {
      const id = await fbAdd('products', data) || `local_${Date.now()}`;
      setProducts(prev => [...prev, { id, ...data }]);
      showToast('Product added!', '🚀');
    }
  }, [showToast]);

  const deleteProduct = useCallback(async (id) => {
    await fbDelete('products', id);
    setProducts(prev => prev.filter(p => p.id !== id));
    showToast('Product deleted.', '🗑️');
  }, [showToast]);

  // Video operations
  const saveVideo = useCallback(async (data) => {
    const id = await fbAdd('videos', data) || `local_${Date.now()}`;
    setVideos(prev => [{ id, ...data }, ...prev]);
    showToast('Video added!', '🚀');
  }, [showToast]);

  const deleteVideo = useCallback(async (id) => {
    await fbDelete('videos', id);
    setVideos(prev => prev.filter(v => v.id !== id));
    showToast('Video deleted.', '🗑️');
  }, [showToast]);

  // Document operations
  const saveDocument = useCallback(async (data, editingId = null) => {
    if (editingId) {
      await fbUpdate('documents', editingId, data);
      setDocuments(prev => prev.map(d => d.id === editingId ? { ...d, ...data } : d));
      showToast('Document updated!', '✅');
      return editingId;
    } else {
      const id = await fbAdd('documents', data);
      if (!id) {
        showToast('Save failed — check Firestore rules.', '❌', 'error');
        return null;
      }
      setDocuments(prev => [{ id, ...data }, ...prev]);
      showToast('Document saved!', '📄');
      return id;
    }
  }, [showToast]);

  const deleteDocument = useCallback(async (id) => {
    await fbDelete('documents', id);
    setDocuments(prev => prev.filter(d => d.id !== id));
    showToast('Document deleted.', '🗑️');
  }, [showToast]);

  // Subscribe
  const subscribe = useCallback(async (name, email) => {
    if (!email || !name) return showToast('Please fill in both fields.', '⚠️', 'warning');
    if (subscribers.find(s => s.email === email)) return showToast('Already subscribed!', 'ℹ️', 'info');
    const id = await fbAdd('subscribers', { name, email }) || `local_${Date.now()}`;
    setSubscribers(prev => [...prev, { id, name, email }]);
    showToast('Subscribed! Welcome aboard 🎉', '🎉', 'success');
  }, [subscribers, showToast]);

  // Contact
  const submitContact = useCallback(async (data) => {
    const id = await fbAdd('contacts', { ...data, read: false }) || `local_${Date.now()}`;
    setContacts(prev => [{ id, ...data, read: false }, ...prev]);
    showToast("Message sent! I'll reply within 48 hours.", '📬', 'success');
  }, [showToast]);

  // Admin login/logout
  const adminLogin = useCallback(async (email, password) => {
    if (!auth || !fbUtils?.signInWithEmailAndPassword) {
      showToast('Firebase not configured.', '⚠️', 'warning');
      return false;
    }
    try {
      await fbUtils.signInWithEmailAndPassword(auth, email, password);
      showToast('Logged in!', '🔐');
      return true;
    } catch {
      showToast('Invalid credentials.', '❌', 'error');
      return false;
    }
  }, [showToast]);

  const adminLogout = useCallback(async () => {
    if (auth && fbUtils?.signOut) await fbUtils.signOut(auth);
    setAdminUser(null);
    showToast('Logged out.', '👋');
  }, [showToast]);

  return (
    <AppContext.Provider value={{
      articles, allArticles, products, videos, documents, subscribers, contacts,
      adminUser, toast,
      showToast, loadData,
      saveArticle, deleteArticle,
      saveProduct, deleteProduct,
      saveVideo, deleteVideo,
      saveDocument, deleteDocument,
      subscribe, submitContact,
      adminLogin, adminLogout,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}
